/**
 * APPLYING THE CRASH ALLOW-LIST TO A SENTRY EVENT (APP-MONITORING M1.4;
 * ADR-0024 decision 2). Found in review by Sameer, 2026-09-07 (F5/F8).
 *
 * THE BUG THIS MODULE EXISTS TO PREVENT. K-PR3 wired both SDKs as
 * `beforeSend(event) { return scrub(event, 'crash') }`. That reads as if it
 * says "let nothing unnamed leave", and it is wrong in a way that looks like
 * success. `scrub`'s crash allow-list is a list of CONTEXT keys — `release`,
 * `instance`, `appRunId`, `screen`, `inCall`, `errorName`. A Sentry event is
 * not a context object: its fields are `event_id`, `exception`, `tags`,
 * `breadcrumbs`, `contexts`, `platform`, `sdk`. Not one of those is on the
 * allow-list, so every one of them was dropped. A real `TypeError` with a
 * stack frame came out the other side as:
 *
 *     {"timestamp":…,"level":"error","release":"api@0.1.0+abc1234"}
 *
 * No exception. No stack. No tags. Events still arrived, issue counts still
 * rose, and every issue was empty — the failure mode of a monitoring system
 * that reports itself healthy while carrying nothing.
 *
 * THE FIX IS NOT TO LOOSEN THE ALLOW-LIST. The allow-list is right; it was
 * simply pointed at the wrong object. The envelope is STRUCTURE — the SDK's
 * own frame, which carries no application payload and must survive intact for
 * an event to be readable at all. The payload rides in a few known maps
 * hanging off it. So the allow-list is applied to those maps, at every depth,
 * and the structure around them is left alone.
 *
 * THE ONE PLACE THE ENVELOPE STILL CARRIES DATA is the exception message.
 * `error.message` reaches Sentry verbatim, and after F10 the app deliberately
 * keeps 14 interpolations of closed vocabularies inside error messages, so
 * "messages carry no data" is a discipline, not a guarantee. The value rules
 * — and only the value rules, never the key allow-list — therefore run over
 * the message: an e-mail, a JWT, an IP, a study code or a bearer token cannot
 * ride out inside one. The message is REPLACED, never deleted, because
 * deleting it would change how Sentry groups the issue.
 */

import { DROPPED, scrub, scrubString } from './scrubber';

export { CRASH_TAG_NAMES, crashTagName } from './scrubber';

/**
 * The maps on a Sentry event that carry application payload. Everything else
 * on the envelope is SDK structure and is passed through untouched.
 *
 * `user` is here so that anything an integration attaches under it meets the
 * allow-list too — no key on the crash list is a user field, so in practice
 * this empties it. `sendDefaultPii: false` should already prevent it; this is
 * the second lock on the same door.
 */
const PAYLOAD_MAPS = ['tags', 'extra', 'contexts', 'request', 'user'] as const;

/**
 * Top-level keys a Sentry event legitimately carries (RFC / SDK protocol).
 * The SDK never puts application data directly on the event object — it goes
 * in one of the PAYLOAD_MAPS above — so anything here that is NOT on this list
 * was put there by a bug or a compromised client, and the tunnel's whole
 * reason for scrubbing a second time is to catch exactly that. Unknown
 * top-level keys are dropped.
 */
const EVENT_STRUCTURE_KEYS = new Set<string>([
  'event_id',
  'timestamp',
  'start_timestamp',
  'platform',
  'level',
  'logger',
  'server_name',
  'release',
  'dist',
  'environment',
  'transaction',
  'transaction_info',
  'fingerprint',
  'modules',
  'sdk',
  'type',
  'message',
  // `captureMessage` puts its text here (with `params` for a template) rather
  // than in `message`. Nothing calls it today; listed so the day something
  // does, the message is not silently deleted by this function.
  'logentry',
  'exception',
  'stacktrace',
  'threads',
  'breadcrumbs',
  'contexts',
  'tags',
  'extra',
  'user',
  'request',
  'sdkProcessingMetadata',
  'debug_meta',
  'spans',
  'measurements',
]);

/**
 * These operate on `object` and mutate in place, returning the SAME reference
 * with the caller's own type. Declaring a structural shape instead would make
 * them reject the SDK's `ErrorEvent`/`Breadcrumb`, which carry no index
 * signature — and a `beforeSend` hook has to hand back exactly the type it was
 * given.
 */

/** The exception payload, as much of it as this module reads. */
interface Exceptionish {
  values?: Array<{ value?: unknown } | undefined> | undefined;
}

/**
 * Sub-keys permitted inside Sentry's `contexts` map for the `crash` sink.
 *
 * F14: The general scrubber applies flat allow-lists at every depth, so adding
 * bare `model` or `version` to COMMON_KEYS would permit those keys anywhere in
 * any payload. Instead, path-aware context allow-listing re-attaches only the
 * explicit sub-keys required by DEC-2 / M0.1 from the incoming contexts.
 *
 * DEC-2 / never-list: `device.name` is REFUSED (user-settable on Android,
 * routinely a person's name) and `device.id` is REFUSED.
 */
const CONTEXT_ALLOW: Readonly<Record<string, readonly string[]>> = {
  device: ['model', 'simulator'],
  os: ['name', 'version'],
};

function restoreAllowedContexts(
  rawContexts: Record<string, unknown>,
  scrubbedContexts: Record<string, unknown>,
): void {
  for (const [contextName, allowedSubKeys] of Object.entries(CONTEXT_ALLOW)) {
    const rawMap = rawContexts[contextName];
    if (!rawMap || typeof rawMap !== 'object' || Array.isArray(rawMap)) continue;

    const outMap = (scrubbedContexts[contextName] as Record<string, unknown>) ?? {};
    for (const subKey of allowedSubKeys) {
      const val = (rawMap as Record<string, unknown>)[subKey];
      if (val === undefined || val === null) continue;
      if (typeof val === 'string') {
        const cleaned = scrubString(val, 'crash');
        if (cleaned !== null) outMap[subKey] = cleaned;
      } else if (typeof val === 'number' || typeof val === 'boolean') {
        outMap[subKey] = val;
      }
    }
    if (Object.keys(outMap).length > 0) {
      scrubbedContexts[contextName] = outMap;
    }
  }
}

/**
 * Scrub one Sentry event for the `crash` sink, in place of scrubbing the
 * envelope. Mutates and returns the event it was given.
 */
export function scrubCrashEvent<T extends object>(event: T): T {
  const fields = event as Record<string, unknown>;

  // Drop any top-level key the protocol does not define — a compromised or
  // buggy client that hangs `patientName` straight off the event object gets
  // caught here, which is the point of scrubbing a second time on the API.
  for (const key of Object.keys(fields)) {
    if (!EVENT_STRUCTURE_KEYS.has(key)) delete fields[key];
  }

  const rawContexts =
    typeof fields.contexts === 'object' &&
    fields.contexts !== null &&
    !Array.isArray(fields.contexts)
      ? (fields.contexts as Record<string, unknown>)
      : undefined;

  for (const key of PAYLOAD_MAPS) {
    const value = fields[key];
    if (value === undefined || value === null) continue;
    fields[key] = scrub(value, 'crash') ?? {};
  }

  if (rawContexts && typeof fields.contexts === 'object' && fields.contexts !== null) {
    restoreAllowedContexts(rawContexts, fields.contexts as Record<string, unknown>);
  }

  // The exception message: value rules only (see the header). Keys are NOT
  // filtered here — `exception.values[].type` and the stack frames are
  // structure, and an event without them is the empty-issue bug this module
  // was written to fix.
  const values = (fields.exception as Exceptionish | undefined)?.values;
  if (Array.isArray(values)) {
    for (const entry of values) {
      if (!entry || typeof entry.value !== 'string') continue;
      entry.value = scrubString(entry.value, 'crash') ?? DROPPED;
    }
  }

  const crumbs = fields.breadcrumbs;
  if (Array.isArray(crumbs)) {
    for (const crumb of crumbs) {
      if (crumb && typeof crumb === 'object') scrubCrashBreadcrumb(crumb as object);
    }
  }

  return event;
}

/**
 * Scrub one breadcrumb for the `crash` sink. A breadcrumb's `category`,
 * `type`, `level` and `timestamp` are structure; its `data` is payload and its
 * `message` is a string this codebase wrote.
 */
export function scrubCrashBreadcrumb<T extends object>(crumb: T): T {
  const fields = crumb as Record<string, unknown>;
  if (fields.data !== undefined && fields.data !== null) {
    fields.data = scrub(fields.data, 'crash') ?? {};
  }
  if (typeof fields.message === 'string') {
    fields.message = scrubString(fields.message, 'crash') ?? DROPPED;
  }
  return crumb;
}
